import type { ArenaFxEvent } from '../sim/arena';
import type { PresentationQualityTier } from './clientPerformance';

export type ArenaFeedbackPriority = 'primary' | 'secondary' | 'decorative';

export interface ArenaPresentationBudget {
  plannedOperations: number;
  pendingEffects: number;
  corpses: number;
  deathParticles: number;
  damageTextWindowMs: number;
  decorativeStride: number;
}

export interface ArenaFxAdmission {
  events: ArenaFxEvent[];
  estimatedOperations: number;
  dropped: Record<ArenaFeedbackPriority, number>;
}

const BUDGETS: Record<PresentationQualityTier, ArenaPresentationBudget> = {
  full: {
    plannedOperations: 256,
    pendingEffects: 96,
    corpses: 48,
    deathParticles: 8,
    damageTextWindowMs: 60,
    decorativeStride: 1,
  },
  reduced: {
    plannedOperations: 160,
    pendingEffects: 72,
    corpses: 24,
    deathParticles: 5,
    damageTextWindowMs: 90,
    decorativeStride: 2,
  },
  constrained: {
    plannedOperations: 96,
    pendingEffects: 48,
    corpses: 12,
    deathParticles: 3,
    damageTextWindowMs: 130,
    decorativeStride: 4,
  },
};

export function presentationBudgetFor(tier: PresentationQualityTier): ArenaPresentationBudget {
  return BUDGETS[tier];
}

export function arenaFxPriority(event: ArenaFxEvent): ArenaFeedbackPriority {
  if (
    (event.kind === 'projectile' && event.gen === 0) ||
    event.kind === 'telegraph' ||
    event.kind === 'meteor' ||
    event.kind === 'vortex-summon'
  ) {
    return 'primary';
  }
  if (event.kind === 'status' || event.kind === 'splinter' || event.kind === 'nova-ray') {
    return 'decorative';
  }
  return 'secondary';
}

export function estimateArenaFxOperations(event: ArenaFxEvent): number {
  switch (event.kind) {
    case 'projectile':
      return 2 + (event.pierced?.length ?? 0) + (event.knockedTo ? 1 : 0);
    case 'chain':
      return 2;
    case 'explosion':
    case 'flame-strike':
    case 'combustion':
      return 1 + event.targets.length;
    case 'fire-field':
      return event.tiles.length;
    case 'ground-damage':
      return event.hits.length;
    case 'nova':
      return 16 + event.targets.length;
    case 'status':
      return event.marks.length;
    case 'nova-ray':
    case 'splinter':
      return 1 + event.targets.length;
    case 'telegraph':
    case 'vortex-summon':
      return 1;
    case 'meteor':
      return 2 + event.targets.length;
  }
}

export function admitArenaFxEvents(
  events: readonly ArenaFxEvent[],
  tier: PresentationQualityTier,
): ArenaFxAdmission {
  const limit = presentationBudgetFor(tier).plannedOperations;
  const admitted = new Set<number>();
  const dropped: Record<ArenaFeedbackPriority, number> = {
    primary: 0,
    secondary: 0,
    decorative: 0,
  };
  let estimatedOperations = 0;

  for (const priority of ['primary', 'secondary', 'decorative'] as const) {
    for (let index = 0; index < events.length; index += 1) {
      const event = events[index]!;
      if (arenaFxPriority(event) !== priority) continue;
      const cost = estimateArenaFxOperations(event);
      if (priority === 'primary' || estimatedOperations + cost <= limit) {
        admitted.add(index);
        estimatedOperations += cost;
      } else {
        dropped[priority] += 1;
      }
    }
  }

  return {
    events: events.filter((_, index) => admitted.has(index)),
    estimatedOperations,
    dropped,
  };
}
